import { round } from "lodash";

import { CareElementFrequencyType } from "@/Flowsheets/Models/CareElementFrequencyType";
import { CareElementOperatorType, CareElementOperatorTypeValue } from "@/Flowsheets/Models/CareElementOperatorType";
import { VitalSignCareElement } from "@/Flowsheets/Models/CareElements/VitalSignCareElement";

/**
 * Helper class for managing care element grid operations.
 */
export class CareElementGridHelper {
    /**
     * Maps a CareElementOperatorType to its corresponding string representation and concat the target.
     * @param operator The operator to map.
     * @param targetRange The target range.
     * @returns The string representation of the operator.
     */
    public static handleTargetLabel(operator?: CareElementOperatorType | number, targetRange?: { LowerBound: number; UpperBound?: number }): string {
        if (!targetRange || operator === undefined || operator === null)
            return "";

        if (operator === CareElementOperatorType.Between && targetRange.UpperBound !== null)
            return `${this.formatTargetNumber(targetRange.LowerBound)} - ${this.formatTargetNumber(targetRange.UpperBound)}`;

        return `${CareElementOperatorTypeValue[operator] ?? ""} ${this.formatTargetNumber(targetRange.LowerBound)}`;
    }

    /**
     * Get the target label based on the operator and target value.
     * This method formats the target value based on its type (boolean, string, or number) and returns a string representation.
     *
     * @param operator The operator to use for formatting.
     * @param target The target value to format.
     * @returns The formatted target label.
     * @remarks If the operator is `Between` or `EqualTo`, it returns the target value directly.
     * If the target is a boolean, it returns "Yes" or "No". If it's a string, it returns the string directly.
     * If it's a number, it formats the number to two decimal places, ensuring whole numbers are displayed with one decimal place (e.g., 2.0).
     */
    public static getTargetLabel(operator: CareElementOperatorType | number, target: boolean | string | null | number | Date): string {
        if (target === null || target === undefined)
            return "";

        let formattedTarget: string;
        if (typeof target === "boolean") {
            formattedTarget = target === true ? cacheStore.GetString("YesLabel") : cacheStore.GetString("NoLabel");
        }
        else if (typeof target === "string") {
            formattedTarget = target;
        }
        else if (typeof target === "number") {
            formattedTarget = Number.isInteger(target)
                ? `${target}`
                : this.formatTargetNumber(target);
        }
        else {
            formattedTarget = "";
        }

        if (operator === CareElementOperatorType.Between || operator === CareElementOperatorType.EqualTo)
            return formattedTarget;

        return `${CareElementOperatorTypeValue[operator] ?? ""} ${formattedTarget}`;
    }

    /**
     * Generates a frequency label based on the provided frequency type.
     * @param frequency The frequency type.
     * @param getText A function to retrieve localized text.
     * @returns The localized frequency label.
     */
    public static handleFrequencyLabel(
        frequency: CareElementFrequencyType | undefined | null,
        getText: (key: string) => string
    ): string {
        if (!frequency)
            return "";

        const enumKey = CareElementFrequencyType[frequency];
        return getText(`Flowsheets_${enumKey}Label`);
    }

    /**
     * Maps a CareElementOperatorType to its corresponding string representation.
     * @param operator The operator to map.
     * @param getText A function to retrieve localized text.
     * @returns The string representation of the operator.
     */
    public static mapOperator(operator: CareElementOperatorType | number, getText: (key: string) => string): string {
        if (operator === undefined || operator === null)
            return "";

        if (operator === CareElementOperatorType.Between)
            return getText(CareElementOperatorTypeValue[operator]);

        return CareElementOperatorTypeValue[operator] ?? "";
    }

    /**
     * Compares a value against one or two targets based on the operator.
     * @param value The value to compare.
     * @param targetRange The target range.
     * @param operator The operator to use for comparison.
     * @returns True if the value is out of range, otherwise false.
     */
    public static isOutOfRange(value: number, targetRange: number, operator: CareElementOperatorType): boolean;
    public static isOutOfRange(value: number, targetRange: { LowerBound: number; UpperBound?: number }, operator: CareElementOperatorType): boolean;
    public static isOutOfRange(value: number, targetRange: { LowerBound: number; UpperBound?: number } | number, operator: CareElementOperatorType): boolean {
        let lowerBoundTarget: number;
        let upperBoundTarget: number | undefined = undefined;
        if (typeof targetRange === "number") {
            lowerBoundTarget = targetRange;
        }
        else {
            lowerBoundTarget = targetRange.LowerBound;
            upperBoundTarget = targetRange.UpperBound;
        }

        switch (operator) {
            case CareElementOperatorType.GreaterThan:
                return value <= lowerBoundTarget;
            case CareElementOperatorType.LessThan:
                return value >= lowerBoundTarget;
            case CareElementOperatorType.GreaterThanOrEqualTo:
                return value < lowerBoundTarget;
            case CareElementOperatorType.LessThanOrEqualTo:
                return value > lowerBoundTarget;
            case CareElementOperatorType.EqualTo:
                return value !== lowerBoundTarget;
            case CareElementOperatorType.Between:
                return upperBoundTarget !== undefined && (value < lowerBoundTarget || value > upperBoundTarget);
            default:
                return false;
        }
    }

    /**
     * Determines if a value is out of range based on the provided care element.
     * @param value The value to check.
     * @param element The care element containing range information, including target values and operator.
     * @returns True if the value is out of range based on the care element's criteria, otherwise false.
     */
    public static isValueOutOfRange(value: number, element: VitalSignCareElement): boolean {
        if (value === undefined || isNaN(value) || element.TargetRange === undefined || isNaN(element.TargetRange?.LowerBound)) {
            return false;
        }
        return this.isOutOfRange(value, element.TargetRange, element.Operator);
    }

    /**
     * Formats a number to a string with max two decimal places, ensuring whole numbers are displayed with one decimal place (e.g. 2.0).
     * @param value The number to format.
     * @returns The formatted number as a string.
     */
    private static formatTargetNumber(value: number): string {
        if (value === null || value === undefined || isNaN(value))
            return "";

        const rounded = round(value, 2);
        return Number.isInteger(rounded) ? `${rounded.toFixed(1)}` : `${rounded}`;
    }
}
